import { Directive, ElementRef, inject, input } from '@angular/core';
import { insertTextAtCursor } from './insert-text-at-cursor.util';

const NAVIGATION_KEYS = new Set([
  'Alt',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'Backspace',
  'Control',
  'Delete',
  'End',
  'Enter',
  'Home',
  'Meta',
  'Shift',
  'Tab',
]);

/**
 * Directive that restricts input to characters matching a given regular
 * expression pattern.
 *
 * Intercepts `keydown` events to block characters that do not match the
 * pattern, `beforeinput` events to catch OS-level text substitutions (e.g.
 * the macOS "double-space → period" feature), and `paste` events to strip
 * non-matching characters from the clipboard text before inserting it at the
 * current cursor position.
 *
 * Navigation and modifier keys (e.g. Backspace, Tab, Arrow keys, Ctrl)
 * are always allowed regardless of the pattern. Keyboard shortcuts that use
 * Ctrl, Meta, or Alt as a modifier (e.g. Ctrl+V, Cmd+A) are also always
 * allowed.
 *
 * @example
 * ```html
 * <input idtAllowChars [allowChars]="/^[a-zA-Z0-9]+$/" />
 * ```
 */
@Directive({
  selector: '[idtAllowChars]',
  standalone: true,
  host: {
    '(beforeinput)': 'onBeforeInput($event)',
    '(keydown)': 'onKeyDown($event)',
    '(paste)': 'onPaste($event)',
  },
})
export class IdtAllowCharsDirective {
  private readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  /**
   * A RegExp pattern that each character is tested against individually.
   * Only characters that match the pattern are allowed.
   *
   * @example /^[a-zA-Z0-9]+$/ — alphanumeric only
   * @example /^[0-9]+$/ — digits only
   */
  readonly allowChars = input<RegExp>();

  /**
   * Intercepts `beforeinput` events to block OS-level text substitutions
   * (e.g. the macOS "double-space → period" feature) that would insert
   * characters not matching the allowed pattern.
   *
   * @param event - The native `InputEvent`.
   */
  onBeforeInput(event: InputEvent): void {
    const pattern = this.allowChars();
    if (!pattern) {
      return;
    }

    const { data } = event;
    if (data !== null && !this.filterAllowedChars(data, pattern)) {
      event.preventDefault();
    }
  }

  /**
   * Blocks the keystroke when the pressed key does not match the
   * allowed-characters pattern. Navigation keys, modifier keys, and any
   * keyboard shortcut that uses Ctrl / Meta / Alt are always permitted.
   *
   * @param event - The native keyboard event.
   */
  onKeyDown(event: KeyboardEvent): void {
    const pattern = this.allowChars();
    if (!pattern) {
      return;
    }

    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (NAVIGATION_KEYS.has(event.key)) {
      return;
    }

    if (!pattern.test(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * Intercepts paste events, filters the clipboard text to keep only
   * characters matching the allowed pattern, and inserts the cleaned
   * result at the current cursor / selection position.
   *
   * Dispatches a synthetic `input` event so that Angular forms and
   * other listeners are notified of the value change.
   *
   * @param event - The native clipboard event.
   */
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pattern = this.allowChars();
    if (!pattern) {
      return;
    }

    const pastedText = event.clipboardData?.getData('text') ?? '';
    const cleaned = this.filterAllowedChars(pastedText, pattern);

    if (!cleaned) {
      return;
    }

    insertTextAtCursor(this.el.nativeElement, cleaned);
  }

  /**
   * Keeps only the characters that match the allowed pattern.
   * Iterates over Unicode code points so surrogate pairs are treated as
   * single characters rather than two UTF-16 code units.
   *
   * @param value - The raw string to filter.
   * @param pattern - The RegExp each character is tested against.
   * @returns A string containing only the allowed characters.
   */
  private filterAllowedChars(value: string, pattern: RegExp): string {
    return Array.from(value)
      .filter((char) => pattern.test(char))
      .join('');
  }
}
